// ═══════════════════════════════════════════════════════════════
// BENEFIT FULFILLMENT PANEL
// Path: src/components/shows/BenefitFulfillmentPanel.jsx
//
// Shared checklist for benefit fulfillment tracking.
// Used in both ShowsView Sponsorships tab and DonorDetailView.
//
// Props:
//   sponsorshipId   — uuid of the sponsorship
//   fulfillments    — array of fulfillment rows (pre-loaded)
//   canEdit         — boolean (Admin/Manager can check/uncheck)
//   canAddCustom    — boolean (Admin/Manager can add custom benefits)
//   chapterId       — for creating custom benefits
//   userId          — current user id
//   onUpdate        — () => void — callback after any change
//   showMessage     — (text, type) => void
//
// Blueprint: CMM_Show_Sponsorship_Blueprint_v1_1.md — Phase 5
// ═══════════════════════════════════════════════════════════════

import React, { useState } from "react";
import { CheckSquare, Square, Plus, CheckCheck, User } from "lucide-react";
import { useApp } from "../../context/AppContext";
import { FONT_SIZES, FONT_FAMILY, ICON_SIZES } from "../../constants/appConfig";
import { ActionButton } from "../ui/ActionButton";
import { fulfillmentsService } from "../../services/showSponsorshipsService";

export const BenefitFulfillmentPanel = ({
  sponsorshipId,
  fulfillments = [],
  canEdit = false,
  canAddCustom = false,
  chapterId,
  userId,
  onUpdate,
  showMessage,
}) => {
  const { palette } = useApp();
  const [customInput, setCustomInput] = useState("");
  const [busy, setBusy] = useState(null); // fulfillment id being toggled

  const pendingCount = fulfillments.filter((f) => !f.fulfilled_at).length;

  // ── Toggle fulfilled ──────────────────────────────────────
  const handleToggle = async (fulfillment) => {
    if (!canEdit || busy) return;
    setBusy(fulfillment.id);
    try {
      if (fulfillment.fulfilled_at) {
        await fulfillmentsService.unmarkFulfilled(fulfillment.id);
      } else {
        await fulfillmentsService.markFulfilled(fulfillment.id, userId);
      }
      onUpdate?.();
    } catch (error) {
      showMessage?.("Failed to update benefit", "error");
    } finally {
      setBusy(null);
    }
  };

  // ── Mark all complete ─────────────────────────────────────
  const handleMarkAll = async () => {
    if (!canEdit || busy) return;
    setBusy("all");
    try {
      await fulfillmentsService.markAllFulfilled(sponsorshipId, userId);
      showMessage?.("All benefits marked complete");
      onUpdate?.();
    } catch (error) {
      showMessage?.("Failed to mark all complete", "error");
    } finally {
      setBusy(null);
    }
  };

  // ── Add custom benefit ────────────────────────────────────
  const handleAddCustom = async () => {
    const name = customInput.trim();
    if (!name || !canAddCustom) return;
    setBusy("custom");
    try {
      await fulfillmentsService.addCustomBenefit(
        sponsorshipId,
        name,
        chapterId,
        userId
      );
      setCustomInput("");
      showMessage?.("Custom benefit added");
      onUpdate?.();
    } catch (error) {
      showMessage?.("Failed to add custom benefit", "error");
    } finally {
      setBusy(null);
    }
  };

  // ── Render ────────────────────────────────────────────────
  return (
    <div>
      {/* Benefit checklist */}
      {fulfillments
        .sort((a, b) => a.sort_order - b.sort_order)
        .map((fulfillment) => {
          const isFulfilled = !!fulfillment.fulfilled_at;
          const isToggling = busy === fulfillment.id;
          const CheckIcon = isFulfilled ? CheckSquare : Square;

          return (
            <div
              key={fulfillment.id}
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: 8,
                padding: "7px 0",
                borderBottom: `1px solid ${palette.cardBorder}20`,
                opacity: isToggling ? 0.6 : 1,
                transition: "opacity 0.2s",
              }}
            >
              {/* Checkbox */}
              <button
                onClick={() => handleToggle(fulfillment)}
                disabled={!canEdit || !!busy}
                style={{
                  background: "none",
                  border: "none",
                  cursor: canEdit ? "pointer" : "default",
                  padding: 0,
                  flexShrink: 0,
                  marginTop: 1,
                  color: isFulfilled ? "#16a34a" : palette.text3,
                  display: "flex",
                }}
              >
                <CheckIcon size={ICON_SIZES.sm} />
              </button>

              {/* Name + badges */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    flexWrap: "wrap",
                  }}
                >
                  <span
                    style={{
                      fontSize: FONT_SIZES.xs,
                      fontFamily: FONT_FAMILY,
                      color: isFulfilled ? palette.text3 : palette.text1,
                      textDecoration: isFulfilled ? "line-through" : "none",
                    }}
                  >
                    {fulfillment.benefit_name}
                  </span>

                  {/* Custom badge */}
                  {fulfillment.is_custom && (
                    <span
                      style={{
                        fontSize: "0.55rem",
                        fontWeight: 600,
                        color: "#f59e0b",
                        background: "#fef3c7",
                        padding: "1px 5px",
                        borderRadius: 6,
                        fontFamily: FONT_FAMILY,
                      }}
                    >
                      custom
                    </span>
                  )}
                </div>

                {/* Fulfilled details */}
                {isFulfilled && (
                  <div
                    style={{
                      fontSize: "0.6rem",
                      color: palette.text3,
                      fontFamily: FONT_FAMILY,
                      marginTop: 1,
                      display: "flex",
                      alignItems: "center",
                      gap: 4,
                    }}
                  >
                    <span>
                      {new Date(fulfillment.fulfilled_at).toLocaleDateString(
                        "en-US",
                        { month: "short", day: "numeric" }
                      )}
                    </span>
                    {fulfillment.notes && (
                      <span style={{ fontStyle: "italic" }}>
                        — {fulfillment.notes}
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}

      {/* Actions row */}
      <div
        style={{
          display: "flex",
          gap: 8,
          marginTop: 10,
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        {/* Mark all complete */}
        {canEdit && pendingCount > 0 && (
          <ActionButton
            label="Mark all complete"
            icon={CheckCheck}
            small
            variant="secondary"
            onClick={handleMarkAll}
            disabled={!!busy}
          />
        )}

        {/* Add custom benefit */}
        {canAddCustom && (
          <div
            style={{
              display: "flex",
              gap: 4,
              flex: 1,
              minWidth: 180,
            }}
          >
            <input
              value={customInput}
              onChange={(e) => setCustomInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleAddCustom();
                }
              }}
              placeholder="+ Add benefit..."
              style={{
                flex: 1,
                padding: "4px 8px",
                borderRadius: 6,
                fontSize: FONT_SIZES.xs,
                fontFamily: FONT_FAMILY,
                outline: "none",
                background: palette.inputBg,
                color: palette.inputColor,
                border: `1px solid ${palette.inputBorder}`,
              }}
            />
            <button
              onClick={handleAddCustom}
              disabled={!customInput.trim() || !!busy}
              style={{
                display: "flex",
                alignItems: "center",
                padding: "4px 8px",
                borderRadius: 6,
                border: "none",
                background: palette.accent1,
                color: "#fff",
                fontSize: FONT_SIZES.xs,
                fontFamily: FONT_FAMILY,
                cursor: customInput.trim() ? "pointer" : "not-allowed",
                opacity: customInput.trim() ? 1 : 0.5,
              }}
            >
              <Plus size={ICON_SIZES.xs} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
